import axios from 'axios';
import toast from 'react-hot-toast';
import i18n from '../i18n';
import { applyApiBaseUrl, retryWithDirectApi, shouldFallbackFromProxy, type ApiAxiosConfig } from './api-fallback';

/**
 * HTTP client for regular user API calls.
 * Uses 'token' from localStorage (user auth).
 * Admin uses a separate client (admin-http.ts).
 */
export const http = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: false,
});

http.interceptors.request.use(
  (config) => {
    applyApiBaseUrl(config as ApiAxiosConfig);
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (!config.headers['Accept-Language']) {
      const lang = i18n.language || localStorage.getItem('language') || 'en';
      config.headers['Accept-Language'] = lang;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config as ApiAxiosConfig | undefined;

    if (config && shouldFallbackFromProxy(error)) {
      return retryWithDirectApi(config, (cfg) => http.request(cfg));
    }

    if (error.response?.status === 503 && error.response?.data?.maintenance) {
      const msg =
        error.response?.data?.message ||
        'The platform is currently under maintenance. Please try again later.';
      toast.error(msg, { id: 'maintenance-mode', duration: 8000 });
      return Promise.reject(error);
    }
    if (error.response?.status === 403 && error.response?.data?.panel_verification_required) {
      const target = error.response.data.panel_verification_required === 'phone' ? '/verify-phone' : '/verify-email';
      if (window.location.pathname !== target) {
        window.location.href = target;
      }
      return Promise.reject(error);
    }
    if (error.response?.status === 403 && (error.response?.data?.email_verification_required || error.response?.data?.phone_verification_required)) {
      const msg = error.response?.data?.message || 'Verification is required before this action.';
      toast.error(msg, { id: 'contact-verification-required', duration: 6000 });
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      if (error.response?.data?.force_logout) {
        toast.error(error.response?.data?.message || 'Your account has been blocked.', { id: 'account-blocked', duration: 6000 });
      }
      const path = window.location.pathname;
      if (path.startsWith('/admin')) {
        return Promise.reject(error);
      }
      const publicPages = ['/', '/login', '/register', '/verify-email', '/forgot-password', '/reset-password', '/features', '/pricing', '/contact', '/about', '/documentation', '/guide', '/apps', '/pos'];
      const isPublicPage =
        publicPages.includes(path)
        || path.startsWith('/apps/')
        || path.startsWith('/pay/')
        || path.startsWith('/page/')
        || /^\/(ar|zh|ko)\/page\//.test(path);
      // Network/CORS failures have no response — never treat as logout.
      if (!isPublicPage && error.response) {
        localStorage.removeItem('token');
        localStorage.removeItem('auth-storage');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default http;
